import { AssetType, Row, Sort2 as Sort } from '@innovyze/lib_am_common';
import {
	DataGrid,
	GridColDef,
	GridFilterItem,
	GridFilterModel,
	GridLogicOperator,
	useGridApiRef,
} from '@innovyze/stylovyze';
import React, { useEffect, useMemo } from 'react';
import {
	changeFiltersSelect,
	changePagination,
	changeSort,
	setRunCompleteReload,
	setTab,
} from '@Actions';
import { useGlobalization } from '@Translations/useGlobalization';

import { useDispatch, useSelector } from 'react-redux';
import Scales from '@Components/Utils/icons/Scales';
import { NoReasultsRisk } from './NoResultsRiskDatagrid.component';
import { TableToolbar } from './TableToolbar.component';
import { useRiskDetailsSelectors } from './useRiskDetailsSelectors';
import { useRiskDetailsColumns } from './TableColumns';
import { selectSelectedRiskRows } from '@Selectors/Risk.selectors';
import { NamespacedStoreState } from '@Types/store.types';
import { useRiskDetailsStates } from './useRiskDetailsStates';
import useRiskDetailsRows from './useRiskDetailsRows';
import useRiskDetailsColumnOrder from './useRiskDetailsColumnOrder';
import useGetRiskDetailsColumnVisibility from './useGetRiskDetailsColumnVisibility';

interface Props {
	configId: string;
	refreshRiskScore: () => void;
}

export const noConditionData = (assetType: string) => {
	// For the moment only Water Distibuion Pipe has no Condition Data but this will expand
	return assetType === 'pipe';
};

const validFilterValues: Record<string, string[]> = {
	CONDITION_DATA: ['0', '1'],
	GRADE: ['0', '1', '2', '3', '4'],
};

export function isValidFilterItem(item: GridFilterItem) {
	return validFilterValues[item.field].includes(item.value) && item.value;
}

export function constructValidFilterModel(
	model: GridFilterModel,
): GridFilterModel {
	const modelCopy: GridFilterModel = { ...model };

	modelCopy.items.map(item => {
		return { ...item, value: isValidFilterItem(item) ? item.value : '' };
	});

	return modelCopy;
}

export interface AssetIdAndType {
	assetId: string;
	assetType: AssetType;
}

export const RiskDetails = ({ configId, refreshRiskScore }: Props) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();

	const {
		tablePage,
		hasFullAccess,
		isLoading,
		isWaiting,
		riskConfig,
		canReload,
		runCompleteReload,
		riskDetailsPagination,
		isWaitingAssetLocation,
		isRiskStoreLoading,
	} = useRiskDetailsSelectors();

	const apiRef = useGridApiRef();

	const {
		setAssetHighlight,
		selectionModel,
		setSelectionModel,
		sortModel,
		setSortModel,
		filterModel,
		setFilterModel,
		paginationModel,
		setPaginationModel,
	} = useRiskDetailsStates();

	const selectedAssets = useSelector<NamespacedStoreState, Row[]>(state =>
		selectSelectedRiskRows(state, selectionModel),
	);

	useEffect(() => {
		dispatch(setTab('details'));
	}, []);

	useEffect(() => {
		if (canReload && runCompleteReload) {
			refreshRiskScore();
			setRunCompleteReload(false);
		}
	}, [runCompleteReload]);

	useEffect(() => {
		setAssetHighlight?.(selectedAssets);
	}, [selectedAssets]);

	const rows = useRiskDetailsRows();

	const { columnOrder, setColumnOrder } = useRiskDetailsColumnOrder();

	const dispatchSortFn = (name: string, desc: boolean) => {
		const sortPayload: Sort = {
			key: name,
			descending: desc,
		};
		setPaginationModel(pm => ({ ...pm, page: 0 }));
		dispatch(changeSort(sortPayload));
	};

	const columns: GridColDef[] = useRiskDetailsColumns(riskConfig);
	const { columnVisibilityModel, setColumnVisibilityModel } =
		useGetRiskDetailsColumnVisibility(configId);

	const filterRef = useMemo(() => React.createRef<HTMLButtonElement>(), []);

	return (
		<DataGrid
			apiRef={apiRef}
			rows={rows}
			onColumnOrderChange={co => {
				const field = co.column.field;
				const targetIndex = co.targetIndex;
				setColumnOrder(prev => {
					const newOrder = [...prev];
					newOrder.splice(prev.indexOf(field), 1);
					newOrder.splice(targetIndex, 0, field);
					return newOrder;
				});
			}}
			columns={
				columnOrder
					.map(field => columns.find(c => c.field === field))
					.filter(c => c) as GridColDef[]
			}
			data-cy="risk-details-table"
			pinnedColumns={{ left: ['ASSET_ID'], right: ['GRADE', 'Menu'] }}
			className={
				isWaiting || isWaitingAssetLocation ? 'wait' : 'ScrollTable'
			}
			emptyState={{
				title: 'No results to show',
				subtitle: (
					<NoReasultsRisk
						adminFullAccess={hasFullAccess}
						configId={configId}
					/>
				),
				icon: <Scales />,
				color: '#4A6067',
			}}
			loading={isLoading || isWaiting || isRiskStoreLoading}
			skeletonRowCount={10}
			pageSizeOptions={[5, 10, 15, 25, 50, 100]}
			paginationMode="server"
			paginationModel={paginationModel}
			onPaginationModelChange={_paginationModel => {
				if (paginationModel.pageSize != _paginationModel.pageSize) {
					_paginationModel.page = 0;
				}
				setPaginationModel(_paginationModel);
				dispatch(
					changePagination({
						page: _paginationModel.page + 1,
						slice: _paginationModel.pageSize,
					}),
				);
			}}
			columnVisibilityModel={columnVisibilityModel}
			onColumnVisibilityModelChange={setColumnVisibilityModel}
			footerHelper={
				isLoading
					? ''
					: t('{{from}}-{{to}} of {{total}}', {
							from:
								(riskDetailsPagination.page - 1) *
									riskDetailsPagination.slice +
								1,
							to:
								riskDetailsPagination.page ==
								riskDetailsPagination.pages
									? riskDetailsPagination.total
									: riskDetailsPagination.page *
									  riskDetailsPagination.slice,
							total: riskDetailsPagination.total,
					  })
			}
			slots={{
				toolbar: TableToolbar,
			}}
			slotProps={{
				panel: {
					// anchorEl: filterRef.current, // Bug 155409: MUI data grids filter anchoring
				},
				toolbar: {
					configId: configId,
					disable: isLoading || isWaiting || isWaitingAssetLocation,
					hasFullAccess: hasFullAccess,
					showFilterPanel: apiRef.current.showFilterPanel,
					refreshRiskScore: refreshRiskScore,
					filterRef: filterRef,
					lastRunCompleted: riskConfig.riskSetting.lastRunCompleted,
				},
				filterPanel: {
					logicOperators: [GridLogicOperator.And],
				},
			}}
			rowCount={tablePage.total}
			pagination
			onRowSelectionModelChange={setSelectionModel}
			disableMultipleColumnsSorting
			onSortModelChange={sm => {
				if (!sm.length) {
					if (!sortModel.length) {
						return sm.push({ field: 'CURRENT_RISK', sort: 'desc' });
					} else {
						const sortedField = sortModel?.[0];
						sortedField.sort =
							sortedField.sort === 'asc' ? 'desc' : 'asc';
						sm.push(sortModel[0]);
					}
				}
				sm.forEach(sort => {
					const key = sort?.field;
					const descending = sort?.sort !== 'asc';
					dispatchSortFn(key, descending);
				});
				if (!sm.length) dispatchSortFn('CURRENT_RISK', true);
				setSortModel(sm);
			}}
			sortingMode="server"
			sortingOrder={['desc', 'asc']}
			sortModel={sortModel}
			filterToolbar
			filterModel={filterModel}
			filterMode="server"
			onFilterModelChange={(model: GridFilterModel) => {
				const tmpModel = constructValidFilterModel(model);
				setFilterModel(tmpModel);

				const filterValueModel = tmpModel.items
					.map(item => ({
						colKey: item.field,
						value: item.value,
					}))
					.filter(item => item.value && item.colKey);

				dispatch(changeFiltersSelect(filterValueModel));
			}}
		/>
	);
};
